import { useEffect, useState } from 'react';

/*
 * Company logos often come with empty margins (transparent or a solid white box around a
 * small mark), so at card size the mark itself looks tiny. trimLogo() loads a logo once,
 * crops those margins, and reports the real aspect ratio so the card can fit it to its box.
 * Results are cached per URL. Anything unexpected falls back to the original file.
 */

const cache = new Map();

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

async function trim(url) {
  // fetch -> blob URL: the canvas stays readable whatever the image cache did before
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error('logo fetch failed');
  const blobUrl = URL.createObjectURL(await res.blob());
  try {
    const img = await loadImage(blobUrl);
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return { src: url, aspect: 1 };
    const scale = Math.min(1, 1200 / Math.max(nw, nh));
    const w = Math.max(1, Math.round(nw * scale));
    const h = Math.max(1, Math.round(nh * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const px = ctx.getImageData(0, 0, w, h).data;

    // Background = the corner color. Transparent corners: trim by alpha. Opaque: trim pixels close to it.
    const corners = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + w - 1) * 4];
    const transparent = corners.filter((i) => px[i + 3] < 16).length >= 3;
    const bg = [px[0], px[1], px[2]];
    const isInk = (i) => (transparent
      ? px[i + 3] > 16
      : px[i + 3] > 16 && Math.max(Math.abs(px[i] - bg[0]), Math.abs(px[i + 1] - bg[1]), Math.abs(px[i + 2] - bg[2])) > 28);

    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (isInk((y * w + x) * 4)) {
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < 0) return { src: url, aspect: nw / nh }; // nothing found: leave it alone
    const pad = Math.round(Math.max(x1 - x0, y1 - y0) * 0.03);
    x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad);
    x1 = Math.min(w - 1, x1 + pad); y1 = Math.min(h - 1, y1 + pad);
    const cw = x1 - x0 + 1;
    const ch = y1 - y0 + 1;
    if (cw >= w * 0.97 && ch >= h * 0.97) return { src: url, aspect: nw / nh }; // no real margin

    const out = document.createElement('canvas');
    out.width = cw;
    out.height = ch;
    out.getContext('2d').drawImage(canvas, x0, y0, cw, ch, 0, 0, cw, ch);
    return { src: out.toDataURL('image/png'), aspect: cw / ch };
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

export function trimLogo(url) {
  if (!cache.has(url)) cache.set(url, trim(url).catch(() => null));
  return cache.get(url);
}

/**
 * { src, aspect } for a logo URL: the original right away, the trimmed version once ready.
 * Local previews (blob: URLs from the editor) are trimmed too.
 */
export function useFittedLogo(url) {
  const [fit, setFit] = useState(null);
  useEffect(() => {
    let active = true;
    setFit(null);
    if (url) trimLogo(url).then((r) => active && r && setFit(r));
    return () => { active = false; };
  }, [url]);
  return fit && url ? fit : { src: url, aspect: null };
}
